/**
 * Hooks para gestión de estructura organizacional
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  unionService,
  associationService,
  districtService,
  churchService,
  clubService,
  unitService,
} from "@/lib/services/organization";
import { useAuthStore } from "@/stores/authStore";
import type { Union, Association, District, Church, Club, Unit } from "@/types";

// ==================== QUERY KEYS ====================

export const organizationKeys = {
  unions: ["unions"] as const,
  union: (id: string) => ["unions", id] as const,
  associations: ["associations"] as const,
  associationsByUnion: (unionId: string) =>
    ["associations", "union", unionId] as const,
  association: (id: string) => ["associations", id] as const,
  districts: ["districts"] as const,
  districtsByAssociation: (associationId: string) =>
    ["districts", "association", associationId] as const,
  district: (id: string) => ["districts", id] as const,
  churches: ["churches"] as const,
  churchesByDistrict: (districtId: string) =>
    ["churches", "district", districtId] as const,
  churchesByAssociation: (associationId: string) =>
    ["churches", "association", associationId] as const,
  church: (id: string) => ["churches", id] as const,
  clubs: ["clubs"] as const,
  clubsByChurch: (churchId: string) => ["clubs", "church", churchId] as const,
  clubsByAssociation: (associationId: string) =>
    ["clubs", "association", associationId] as const,
  club: (id: string) => ["clubs", id] as const,
  units: ["units"] as const,
  unitsByClub: (clubId: string) => ["units", "club", clubId] as const,
};

// ==================== UNION HOOKS ====================

export function useUnions() {
  return useQuery({
    queryKey: organizationKeys.unions,
    queryFn: () => unionService.getAll(),
  });
}

export function useUnion(id: string) {
  return useQuery({
    queryKey: organizationKeys.union(id),
    queryFn: () => unionService.getById(id),
    enabled: !!id,
  });
}

export function useUpdateUnion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Union> }) =>
      unionService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.unions });
      queryClient.invalidateQueries({ queryKey: organizationKeys.union(id) });
      toast.success("Unión actualizada");
    },
    onError: () => {
      toast.error("Error al actualizar la unión");
    },
  });
}

// ==================== ASSOCIATION HOOKS ====================

export function useAssociations() {
  return useQuery({
    queryKey: organizationKeys.associations,
    queryFn: () => associationService.getAll(),
  });
}

export function useAssociationsByUnion(unionId: string) {
  return useQuery({
    queryKey: organizationKeys.associationsByUnion(unionId),
    queryFn: () => associationService.getByUnion(unionId),
    enabled: !!unionId,
  });
}

export function useAssociation(id: string) {
  return useQuery({
    queryKey: organizationKeys.association(id),
    queryFn: () => associationService.getById(id),
    enabled: !!id,
  });
}

export function useCreateAssociation() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<Association, "id" | "createdAt" | "updatedAt">) =>
      associationService.create(data, user?.uid || ""),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.associations,
      });
      // También invalidar la query filtrada por unión
      if ("unionId" in variables) {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.associationsByUnion(
            variables.unionId as string,
          ),
        });
      }
      toast.success("Asociación creada");
    },
    onError: (error) => {
      console.error("Error creating association:", error);
      toast.error("Error al crear la asociación");
    },
  });
}

export function useUpdateAssociation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Association> }) =>
      associationService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.associations,
      });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.association(id),
      });
      toast.success("Asociación actualizada");
    },
    onError: () => {
      toast.error("Error al actualizar la asociación");
    },
  });
}

export function useDeleteAssociation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => associationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.associations,
      });
      toast.success("Asociación eliminada");
    },
    onError: () => {
      toast.error("Error al eliminar la asociación");
    },
  });
}

// ==================== DISTRICT HOOKS ====================

export function useDistricts() {
  return useQuery({
    queryKey: organizationKeys.districts,
    queryFn: () => districtService.getAll(),
  });
}

export function useDistrictsByAssociation(associationId: string) {
  return useQuery({
    queryKey: organizationKeys.districtsByAssociation(associationId),
    queryFn: () => districtService.getByAssociation(associationId),
    enabled: !!associationId,
  });
}

export function useDistrict(id: string) {
  return useQuery({
    queryKey: organizationKeys.district(id),
    queryFn: () => districtService.getById(id),
    enabled: !!id,
  });
}

export function useCreateDistrict() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<District, "id" | "createdAt" | "updatedAt">) =>
      districtService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.districts });
      toast.success("Distrito creado");
    },
    onError: () => {
      toast.error("Error al crear el distrito");
    },
  });
}

export function useUpdateDistrict() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<District> }) =>
      districtService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.districts });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.district(id),
      });
      toast.success("Distrito actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el distrito");
    },
  });
}

export function useDeleteDistrict() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => districtService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.districts });
      toast.success("Distrito eliminado");
    },
    onError: () => {
      toast.error("Error al eliminar el distrito");
    },
  });
}

// ==================== CHURCH HOOKS ====================

export function useChurches() {
  return useQuery({
    queryKey: organizationKeys.churches,
    queryFn: () => churchService.getAll(),
  });
}

export function useChurchesByDistrict(districtId: string) {
  return useQuery({
    queryKey: organizationKeys.churchesByDistrict(districtId),
    queryFn: () => churchService.getByDistrict(districtId),
    enabled: !!districtId,
  });
}

export function useChurchesByAssociation(associationId: string | undefined) {
  return useQuery({
    queryKey: organizationKeys.churchesByAssociation(associationId || ""),
    queryFn: () => churchService.getByAssociation(associationId!),
    enabled: !!associationId,
  });
}

export function useChurch(id: string) {
  return useQuery({
    queryKey: organizationKeys.church(id),
    queryFn: () => churchService.getById(id),
    enabled: !!id,
  });
}

export function useCreateChurch() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<Church, "id" | "createdAt" | "updatedAt">) =>
      churchService.create(data, user?.uid || ""),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.churches });
      // También invalidar iglesias por asociación
      if (data.associationId) {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.churchesByAssociation(data.associationId),
        });
      }
      toast.success("Iglesia creada exitosamente");
    },
    onError: (error) => {
      console.error("Error creating church:", error);
      toast.error(
        `Error al crear la iglesia: ${error instanceof Error ? error.message : "Error desconocido"}`,
      );
    },
  });
}

export function useUpdateChurch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Church> }) =>
      churchService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.churches });
      queryClient.invalidateQueries({ queryKey: organizationKeys.church(id) });
      toast.success("Iglesia actualizada");
    },
    onError: () => {
      toast.error("Error al actualizar la iglesia");
    },
  });
}

export function useDeleteChurch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => churchService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.churches });
      toast.success("Iglesia eliminada");
    },
    onError: () => {
      toast.error("Error al eliminar la iglesia");
    },
  });
}

// ==================== CLUB HOOKS ====================

export function useClubs() {
  return useQuery({
    queryKey: organizationKeys.clubs,
    queryFn: () => clubService.getAll(),
  });
}

export function useClubsByChurch(churchId: string) {
  return useQuery({
    queryKey: organizationKeys.clubsByChurch(churchId),
    queryFn: () => clubService.getByChurch(churchId),
    enabled: !!churchId,
  });
}

export function useClubsByAssociation(associationId: string) {
  return useQuery({
    queryKey: organizationKeys.clubsByAssociation(associationId),
    queryFn: () => clubService.getByAssociation(associationId),
    enabled: !!associationId,
  });
}

export function useClub(id: string) {
  return useQuery({
    queryKey: organizationKeys.club(id),
    queryFn: () => clubService.getById(id),
    enabled: !!id,
  });
}

export function useCreateClub() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<Club, "id" | "createdAt" | "updatedAt">) =>
      clubService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.clubs });
      // También invalidar accessible-clubs para que se actualice en vistas de asociación
      queryClient.invalidateQueries({ queryKey: ["accessible-clubs"] });
      toast.success("Club creado exitosamente");
    },
    onError: (error) => {
      console.error("Error creating club:", error);
      toast.error(
        `Error al crear el club: ${error instanceof Error ? error.message : "Error desconocido"}`,
      );
    },
  });
}

export function useUpdateClub() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Club> }) =>
      clubService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.clubs });
      queryClient.invalidateQueries({ queryKey: organizationKeys.club(id) });
      toast.success("Club actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el club");
    },
  });
}

export function useDeleteClub() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => clubService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.clubs });
      toast.success("Club eliminado");
    },
    onError: () => {
      toast.error("Error al eliminar el club");
    },
  });
}

// ==================== UNIT HOOKS ====================

export function useUnitsByClub(clubId: string) {
  return useQuery({
    queryKey: organizationKeys.unitsByClub(clubId),
    queryFn: () => unitService.getByClub(clubId),
    enabled: !!clubId,
  });
}

export function useCreateUnit() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<Unit, "id" | "createdAt" | "updatedAt">) =>
      unitService.create(data, user?.uid || ""),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.unitsByClub(data.clubId),
      });
      toast.success("Unidad creada");
    },
    onError: () => {
      toast.error("Error al crear la unidad");
    },
  });
}

export function useUpdateUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Unit>;
      clubId: string;
    }) => unitService.update(id, data),
    onSuccess: (_, { clubId }) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.unitsByClub(clubId),
      });
      toast.success("Unidad actualizada");
    },
    onError: () => {
      toast.error("Error al actualizar la unidad");
    },
  });
}

export function useDeleteUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; clubId: string }) =>
      unitService.delete(id),
    onSuccess: (_, { clubId }) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.unitsByClub(clubId),
      });
      toast.success("Unidad eliminada");
    },
    onError: () => {
      toast.error("Error al eliminar la unidad");
    },
  });
}
